import { APIScope } from "@swivl-mono/types";
import { LayoutWithSidebar } from "@swivl-mono/ui";
import { useMemo } from "react";
import { Outlet } from "react-router-dom";

import { useHasPermission } from "../../../Helpers/Permissions/useHasPermission";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import { useScheduleGroups } from "../CortexV3/Components/ScheduleEditor/useScheduleGroups";
import { useLocationFmsCapabilities } from "./Hooks/useLocationFmsCapabilities";
import { useScriptsInForce } from "./Hooks/useScriptsInForce";
import { useSequenceConfig } from "./Hooks/useSequenceConfig";
import { SequenceTypeList } from "./SequenceTypeList";
import { SequencesV3Provider } from "./SequencesV3.Context";
import { sequenceScopeForPage } from "./sequenceScope";

/**
 * Property Manager v3 → Sequences (ENG-3216). The editor for the new sequence engine's
 * config (`bot_sequence_config`) at org, group or location scope: a left column of sequence
 * types, and the selected type's settings on the right (`:sequenceSlug`). Everything here is
 * a setting the engine acts on; the hours themselves live on the Hours page, and "Send hours"
 * picks which schedule group a type follows (decision 50).
 */
export function SequencesV3View(): React.ReactElement | null {
  const page = usePropertiesGetLocationIds();
  const orgId = Member.orgId;
  const scope = useMemo(() => sequenceScopeForPage(page, orgId ?? 0), [page, orgId]);
  const canEdit = useHasPermission(APIScope.SEQUENCES_EDIT, page.groupId, page.locationId);
  const config = useSequenceConfig({ scope: scope.api, scopeKind: scope.kind });
  const fms = useLocationFmsCapabilities({ scope: scope.api });
  const scheduleGroups = useScheduleGroups(orgId);
  const scripts = useScriptsInForce(scope);

  if (!orgId) return null;

  return (
    <SequencesV3Provider value={{ scope, config, fms, scheduleGroups: scheduleGroups.groups, scripts, canEdit }}>
      <LayoutWithSidebar
        sidebar={<SequenceTypeList basePath={`${page.preamble}/page/sequences`} />}
        contentNoPadding={true}
        content={
          <div className="min-h-full bg-grey-50">
            <Outlet />
          </div>
        }
      />
    </SequencesV3Provider>
  );
}
